/**
 * Blog Publish Validation Checklist
 *
 * Pre-publish gate that validates blog posts for SEO, LLM discoverability,
 * content structure, and technical requirements before allowing publish.
 *
 * Required items block publishing. Recommended items show as warnings.
 * Automated checks run on content; manual checks require author confirmation.
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight,
  Send, X, ExternalLink, Shield,
} from 'lucide-react';

// ── Types ──

export interface PostData {
  title: string;
  slug: string;
  excerpt: string;
  contentHtml: string;
  seoTitle: string;
  seoDescription: string;
  author: string;
  authorId: string | null;
  category: string;
  heroImageUrl: string;
  tags: string[];
  socialTitle: string;
  socialDescription: string;
  socialImageUrl: string;
  socialImageAlt: string;
  canonicalUrlOverride: string;
}

type CheckStatus = 'pass' | 'fail' | 'warning';

interface ValidationCheck {
  id: string;
  label: string;
  description: string;
  status: CheckStatus;
  required: boolean;
  detail?: string;
  automated: boolean;
}

interface CheckSection {
  id: string;
  title: string;
  checks: ValidationCheck[];
}

// ── Content Analysis Helpers ──

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function countWords(html: string): number {
  const text = stripHtml(html).trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function parseHeadings(html: string): { level: number; text: string }[] {
  const div = document.createElement('div');
  div.innerHTML = html;
  const headings: { level: number; text: string }[] = [];
  div.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
    const level = parseInt(el.tagName[1]);
    headings.push({ level, text: el.textContent || '' });
  });
  return headings;
}

function checkHeadingHierarchy(html: string): { valid: boolean; detail: string } {
  const headings = parseHeadings(html);
  if (headings.length === 0) return { valid: false, detail: 'No headings found in content' };

  // Check for H1 in content (title is the H1, content should only have H2+)
  const h1Count = headings.filter(h => h.level === 1).length;
  if (h1Count > 0) return { valid: false, detail: `Found ${h1Count} H1 tag(s) in content body. The post title is the H1 — use H2/H3/H4 in the body.` };

  // Check for skipped levels (e.g., H2 -> H4 without H3)
  for (let i = 1; i < headings.length; i++) {
    const prev = headings[i - 1].level;
    const curr = headings[i].level;
    if (curr > prev + 1) {
      return { valid: false, detail: `Skipped heading level: H${prev} to H${curr}. Use sequential nesting (H2 > H3 > H4).` };
    }
  }

  return { valid: true, detail: `${headings.length} heading(s), properly nested` };
}

function countLinks(html: string): { internal: number; external: number; internalUrls: string[]; externalUrls: string[] } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const anchors = div.querySelectorAll('a[href]');
  let internal = 0;
  let external = 0;
  const internalUrls: string[] = [];
  const externalUrls: string[] = [];

  anchors.forEach(a => {
    const href = a.getAttribute('href') || '';
    if (href.includes('coachcedar.com') || href.startsWith('/')) {
      internal++;
      internalUrls.push(href);
    } else if (href.startsWith('http')) {
      external++;
      externalUrls.push(href);
    }
  });

  return { internal, external, internalUrls, externalUrls };
}

function checkImagesAlt(html: string): { allHaveAlt: boolean; total: number; missing: number; details: string[] } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const images = div.querySelectorAll('img');
  const total = images.length;
  let missing = 0;
  const details: string[] = [];

  images.forEach(img => {
    const alt = img.getAttribute('alt')?.trim();
    const src = img.getAttribute('src') || 'unknown';
    if (!alt) {
      missing++;
      const filename = src.split('/').pop() || src;
      details.push(filename);
    }
  });

  return { allHaveAlt: missing === 0, total, missing, details };
}

function checkSlug(slug: string): { valid: boolean; issues: string[] } {
  const issues: string[] = [];
  if (!slug) {
    issues.push('Slug is empty');
    return { valid: false, issues };
  }
  if (slug.length > 75) issues.push('Slug is very long (>75 chars)');
  if (/\d{4,}/.test(slug)) issues.push('Contains numeric ID or date — prefer keyword-based slugs');
  const fillerWords = ['the', 'and', 'of', 'for', 'in', 'on', 'at', 'to', 'a', 'an', 'is', 'it'];
  const parts = slug.split('-');
  const fillerFound = parts.filter(p => fillerWords.includes(p));
  if (fillerFound.length > 2) issues.push(`Contains filler words: ${fillerFound.join(', ')}`);
  if (/[A-Z]/.test(slug)) issues.push('Contains uppercase characters');
  if (/[_\s]/.test(slug)) issues.push('Contains underscores or spaces — use hyphens');

  return { valid: issues.length === 0, issues };
}

// ── Main Validation Function ──

function runValidation(post: PostData, authors: { id: string; bio: string | null }[]): CheckSection[] {
  const wordCount = countWords(post.contentHtml);
  const headingCheck = checkHeadingHierarchy(post.contentHtml);
  const links = countLinks(post.contentHtml);
  const imageAlt = checkImagesAlt(post.contentHtml);
  const slugCheck = checkSlug(post.slug);
  const effectiveSeoTitle = post.seoTitle || post.title;
  const effectiveMetaDesc = post.seoDescription || post.excerpt;

  // Find author bio from the authors database
  const authorRecord = authors.find(a => a.id === post.authorId);
  const hasBio = !!authorRecord?.bio;

  const sections: CheckSection[] = [
    // ── 1. Page Metadata ──
    {
      id: 'metadata',
      title: 'Page Metadata',
      checks: [
        {
          id: 'title_length',
          label: 'Title tag length',
          description: 'Unique, 50-60 characters. Primary keyword near the front.',
          status: effectiveSeoTitle.length >= 50 && effectiveSeoTitle.length <= 60 ? 'pass'
            : effectiveSeoTitle.length >= 40 && effectiveSeoTitle.length <= 70 ? 'warning' : 'fail',
          required: true,
          detail: `${effectiveSeoTitle.length} characters (target: 50-60)`,
          automated: true,
        },
        {
          id: 'meta_description',
          label: 'Meta description',
          description: '120-155 characters. Compelling reason to click, not a summary.',
          status: !effectiveMetaDesc ? 'fail'
            : effectiveMetaDesc.length >= 120 && effectiveMetaDesc.length <= 155 ? 'pass'
            : effectiveMetaDesc.length >= 100 && effectiveMetaDesc.length <= 170 ? 'warning' : 'fail',
          required: true,
          detail: effectiveMetaDesc
            ? `${effectiveMetaDesc.length} characters (target: 120-155)`
            : 'No meta description set',
          automated: true,
        },
        {
          id: 'canonical_url',
          label: 'Canonical URL',
          description: 'Set and pointing to the correct URL.',
          status: post.slug ? 'pass' : 'fail',
          required: true,
          detail: post.canonicalUrlOverride
            ? `Custom: ${post.canonicalUrlOverride}`
            : `Auto: coachcedar.com/blog/${post.slug}`,
          automated: true,
        },
        {
          id: 'url_slug',
          label: 'URL slug',
          description: 'Short, readable, hyphen-separated. Contains primary keyword.',
          status: !post.slug ? 'fail' : slugCheck.valid ? 'pass' : 'warning',
          required: true,
          detail: slugCheck.issues.length > 0
            ? slugCheck.issues.join('. ')
            : `/${post.slug}`,
          automated: true,
        },
        {
          id: 'language_tag',
          label: 'Language tag',
          description: '<html lang="en"> set correctly.',
          status: 'pass',
          required: true,
          detail: 'Auto-set by site template',
          automated: true,
        },
      ],
    },

    // ── 2. Content Structure ──
    {
      id: 'content',
      title: 'Content Structure',
      checks: [
        {
          id: 'single_h1',
          label: 'Single H1',
          description: 'The post title serves as the page H1. No H1 tags should appear in the body.',
          status: !post.title ? 'fail'
            : parseHeadings(post.contentHtml).some(h => h.level === 1) ? 'fail' : 'pass',
          required: true,
          detail: post.title
            ? parseHeadings(post.contentHtml).some(h => h.level === 1)
              ? 'H1 found in body content — remove it (title is the H1)'
              : `H1: "${post.title}"`
            : 'No title set',
          automated: true,
        },
        {
          id: 'heading_hierarchy',
          label: 'Heading hierarchy',
          description: 'Proper nesting: H2 > H3 > H4. No skipped levels.',
          status: headingCheck.valid ? 'pass' : 'fail',
          required: true,
          detail: headingCheck.detail,
          automated: true,
        },
        {
          id: 'opening_paragraph',
          label: 'Opening paragraph has primary keyword',
          description: 'First 100 words include the primary keyword and clearly state what the post covers. Critical for LLM engines.',
          status: 'warning', // Manual check — defaults to warning
          required: true,
          detail: 'Verify manually: does the opening paragraph contain the target keyword?',
          automated: false,
        },
        {
          id: 'subheadings_keywords',
          label: 'Subheadings use keyword variations',
          description: 'H2s and H3s use natural variations of target keywords where appropriate.',
          status: 'warning',
          required: false,
          detail: 'Review subheadings for natural keyword inclusion',
          automated: false,
        },
        {
          // Demoted from required → recommended (2026-05-29) so short-form
          // product update / announcement posts don't get gated. SEO-pillar
          // content should still aim for ≥800; the warning surfaces the gap
          // without blocking publish.
          id: 'word_count',
          label: 'Word count',
          description: 'Minimum 800 words for standard posts. 1,500+ for pillar content. Short product updates are exempt.',
          status: wordCount >= 800 ? 'pass' : wordCount >= 600 ? 'warning' : 'fail',
          required: false,
          detail: `${wordCount.toLocaleString()} words${wordCount < 800 ? ` (recommended ≥800 for SEO posts)` : wordCount >= 1500 ? ' (pillar-length)' : ''}`,
          automated: true,
        },
        {
          // Demoted from required → recommended (2026-05-29). Same rationale:
          // self-contained announcement posts often have no internal links
          // to reach for and shouldn't be gated on this.
          id: 'internal_links',
          label: 'Internal links',
          description: 'At least 2 internal links to other Coach Cedar pages. Builds topical authority. Optional on announcements.',
          status: links.internal >= 2 ? 'pass' : links.internal === 1 ? 'warning' : 'fail',
          required: false,
          detail: `${links.internal} internal link(s) found${links.internal < 2 ? ` (recommended ≥2 for SEO posts)` : ''}`,
          automated: true,
        },
        {
          id: 'external_links',
          label: 'External links',
          description: 'At least 1 link to a credible external source. Signals trustworthiness.',
          status: links.external >= 1 ? 'pass' : 'warning',
          required: false,
          detail: `${links.external} external link(s) found`,
          automated: true,
        },
        {
          id: 'concluding_summary',
          label: 'Concluding summary',
          description: 'Final section restates the key takeaway in 2-3 sentences. LLMs often pull from conclusions.',
          status: 'warning',
          required: true,
          detail: 'Verify manually: does the post end with a clear takeaway summary?',
          automated: false,
        },
      ],
    },

    // ── 3. LLM & AI Engine Discoverability ──
    {
      id: 'llm',
      title: 'LLM & AI Engine Discoverability',
      checks: [
        {
          id: 'direct_answers',
          label: 'Clear, direct answers',
          description: 'If the post addresses a question, include a concise answer (40-60 words) early. LLMs prefer direct answers.',
          status: 'warning',
          required: true,
          detail: 'Verify: is there a clear, quotable answer early in the post?',
          automated: false,
        },
        {
          id: 'structured_definitions',
          label: 'Structured definitions',
          description: 'When introducing a concept, define it plainly in one sentence before elaborating.',
          status: 'warning',
          required: false,
          detail: 'Review: are key concepts defined in standalone sentences?',
          automated: false,
        },
        {
          id: 'sourced_claims',
          label: 'Factual claims are sourced',
          description: 'Statistics, study references, and factual claims include citations or links.',
          status: 'warning',
          required: true,
          detail: 'Verify: are all factual claims backed by a source link or citation?',
          automated: false,
        },
        {
          id: 'author_expertise',
          label: 'Author and expertise signals',
          description: 'Author name, bio, and credentials visible. Feeds E-E-A-T signals.',
          status: post.authorId && hasBio ? 'pass' : post.authorId ? 'warning' : 'fail',
          required: true,
          detail: !post.authorId
            ? 'No author selected — assign an author with a bio'
            : !hasBio
            ? 'Author has no bio — add one for E-E-A-T signals'
            : `Author: ${post.author}`,
          automated: true,
        },
        {
          id: 'dates_visible',
          label: 'Published and modified dates visible',
          description: 'Dates displayed on the page and in structured data. LLMs deprioritize undated content.',
          status: 'pass',
          required: true,
          detail: 'Auto-set by CMS on publish',
          automated: true,
        },
        {
          id: 'no_content_gating',
          label: 'No content gating',
          description: 'Full article accessible without login, paywall, or JavaScript rendering.',
          status: 'pass',
          required: true,
          detail: 'Public blog has no content gating',
          automated: true,
        },
        {
          id: 'conversational_phrasing',
          label: 'Conversational phrasing',
          description: 'Include at least one sentence that mirrors how a real person would ask the question.',
          status: 'warning',
          required: false,
          detail: 'e.g., "What happens when you miss a workout?"',
          automated: false,
        },
      ],
    },

    // ── 4. Structured Data (JSON-LD) ──
    {
      id: 'structured_data',
      title: 'Structured Data (JSON-LD)',
      checks: [
        {
          id: 'article_schema',
          label: 'BlogPosting schema',
          description: 'JSON-LD with headline, author, publisher, dates, and image.',
          status: 'pass',
          required: true,
          detail: 'Auto-generated by CMS with all required fields',
          automated: true,
        },
        {
          id: 'schema_image',
          label: 'Schema image',
          description: 'Points to the primary image of the post.',
          status: post.heroImageUrl || post.socialImageUrl ? 'pass' : 'fail',
          required: true,
          detail: post.heroImageUrl
            ? 'Hero image will be used in schema'
            : post.socialImageUrl
            ? 'Social image will be used in schema'
            : 'No image set — add a hero or social image',
          automated: true,
        },
      ],
    },

    // ── 5. Images ──
    {
      id: 'images',
      title: 'Images',
      checks: [
        {
          id: 'alt_text',
          label: 'Alt text on every image',
          description: 'Descriptive, keyword-relevant alt text on all images.',
          status: imageAlt.total === 0 ? 'pass' : imageAlt.allHaveAlt ? 'pass' : 'fail',
          required: true,
          detail: imageAlt.total === 0
            ? 'No images in content'
            : imageAlt.allHaveAlt
            ? `All ${imageAlt.total} image(s) have alt text`
            : `${imageAlt.missing} of ${imageAlt.total} image(s) missing alt text: ${imageAlt.details.join(', ')}`,
          automated: true,
        },
        {
          id: 'hero_image',
          label: 'Hero image set',
          description: 'At least one relevant image visible above the fold.',
          status: post.heroImageUrl ? 'pass' : 'warning',
          required: false,
          detail: post.heroImageUrl ? 'Hero image set' : 'No hero image — consider adding one',
          automated: true,
        },
      ],
    },

    // ── 6. Social & Distribution ──
    {
      id: 'distribution',
      title: 'Social & Distribution',
      checks: [
        {
          id: 'social_image',
          label: 'Social share image',
          description: 'OG image set for link previews on LinkedIn, Facebook, X.',
          status: post.socialImageUrl || post.heroImageUrl ? 'pass' : 'warning',
          required: false,
          detail: post.socialImageUrl
            ? 'Custom social image set'
            : post.heroImageUrl
            ? 'Will fall back to hero image'
            : 'No social image — link previews will use default',
          automated: true,
        },
        {
          id: 'social_preview_validated',
          label: 'Social share previews validated',
          description: 'Checked with LinkedIn Post Inspector, Facebook Debugger, and X Card Validator.',
          status: 'warning',
          required: true,
          detail: 'After publishing, validate previews using the tools linked below',
          automated: false,
        },
        {
          id: 'excerpt_set',
          label: 'Excerpt/description set',
          description: 'Used for post cards, social previews, and search results.',
          status: post.excerpt && post.excerpt.length > 20 ? 'pass' : 'fail',
          required: true,
          detail: post.excerpt
            ? `${post.excerpt.length} characters`
            : 'No excerpt set',
          automated: true,
        },
      ],
    },
  ];

  return sections;
}

// ── Component ──

interface PublishChecklistProps {
  postData: PostData;
  authors: { id: string; bio: string | null }[];
  onPublish: () => void;
  onClose: () => void;
  isPublished: boolean;
}

export const PublishChecklist: React.FC<PublishChecklistProps> = ({
  postData, authors, onPublish, onClose, isPublished,
}) => {
  const [manualChecks, setManualChecks] = useState<Record<string, boolean>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const sections = useMemo(() => runValidation(postData, authors), [postData, authors]);

  // Apply manual check overrides
  const resolvedSections = useMemo(() => {
    return sections.map(section => ({
      ...section,
      checks: section.checks.map(check => {
        if (!check.automated && manualChecks[check.id]) {
          return { ...check, status: 'pass' as CheckStatus };
        }
        return check;
      }),
    }));
  }, [sections, manualChecks]);

  // Compute summary
  const allChecks = resolvedSections.flatMap(s => s.checks);
  const requiredFailing = allChecks.filter(c => c.required && c.status === 'fail');
  const requiredWarning = allChecks.filter(c => c.required && c.status === 'warning');
  const recommendedWarning = allChecks.filter(c => !c.required && c.status !== 'pass');
  const totalPassing = allChecks.filter(c => c.status === 'pass').length;
  const canPublish = requiredFailing.length === 0 && requiredWarning.length === 0;

  const toggleSection = (id: string) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleManualCheck = (id: string) => {
    setManualChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', width: '640px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Shield size={20} style={{ color: '#2E5E34' }} />
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#111827', margin: 0 }}>
                Publish Validation
              </h2>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                {totalPassing}/{allChecks.length} checks passing
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            color: '#9ca3af',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Summary banner */}
        <div style={{
          padding: '12px 24px',
          background: canPublish ? '#f0fdf4' : '#fef2f2',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {canPublish ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle2 size={16} style={{ color: '#166534' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>
                All required checks passed.{recommendedWarning.length > 0 ? ` ${recommendedWarning.length} recommendation(s) to review.` : ' Ready to publish.'}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <XCircle size={16} style={{ color: '#991b1b' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b' }}>
                {requiredFailing.length + requiredWarning.length} required item(s) need attention before publishing.
              </span>
            </div>
          )}
        </div>

        {/* Scrollable checklist */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {resolvedSections.map(section => {
            const sectionPassing = section.checks.filter(c => c.status === 'pass').length;
            const sectionTotal = section.checks.length;
            const sectionHasIssues = section.checks.some(c => c.status !== 'pass');
            const isCollapsed = collapsedSections[section.id] ?? false;

            return (
              <div key={section.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isCollapsed ? <ChevronRight size={14} style={{ color: '#9ca3af' }} /> : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{section.title}</span>
                  </div>
                  <span style={{
                    fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '10px',
                    background: sectionHasIssues ? '#fef3c7' : '#f0fdf4',
                    color: sectionHasIssues ? '#92400e' : '#166534',
                  }}>
                    {sectionPassing}/{sectionTotal}
                  </span>
                </button>

                {/* Section checks */}
                {!isCollapsed && (
                  <div style={{ padding: '0 24px 12px 24px' }}>
                    {section.checks.map(check => (
                      <div
                        key={check.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '10px',
                          padding: '8px 0', borderTop: '1px solid #f9fafb',
                        }}
                      >
                        {/* Status icon / manual checkbox */}
                        {!check.automated && check.status !== 'pass' ? (
                          <input
                            type="checkbox"
                            checked={manualChecks[check.id] || false}
                            onChange={() => toggleManualCheck(check.id)}
                            style={{
                              marginTop: '2px', width: '16px', height: '16px',
                              accentColor: '#2E5E34', cursor: 'pointer', flexShrink: 0,
                            }}
                            title="Check to confirm you've verified this item"
                          />
                        ) : (
                          <div style={{ marginTop: '1px', flexShrink: 0 }}>
                            {check.status === 'pass' && <CheckCircle2 size={16} style={{ color: '#16a34a' }} />}
                            {check.status === 'fail' && <XCircle size={16} style={{ color: '#dc2626' }} />}
                            {check.status === 'warning' && <AlertTriangle size={16} style={{ color: '#d97706' }} />}
                          </div>
                        )}

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              fontSize: '12px', fontWeight: 600,
                              color: check.status === 'pass' ? '#374151' : check.status === 'fail' ? '#991b1b' : '#92400e',
                            }}>
                              {check.label}
                            </span>
                            {!check.required && (
                              <span style={{
                                fontSize: '9px', fontWeight: 600, padding: '1px 5px',
                                borderRadius: '3px', background: '#f3f4f6', color: '#9ca3af',
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                              }}>
                                Recommended
                              </span>
                            )}
                            {!check.automated && (
                              <span style={{
                                fontSize: '9px', fontWeight: 600, padding: '1px 5px',
                                borderRadius: '3px', background: '#eff6ff', color: '#3b82f6',
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                              }}>
                                Manual
                              </span>
                            )}
                          </div>
                          {check.detail && (
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0', lineHeight: 1.4 }}>
                              {check.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Helpful links section */}
          <div style={{ padding: '16px 24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>
              Post-Publish Validation Tools
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                { label: 'LinkedIn Post Inspector', url: 'https://www.linkedin.com/post-inspector/' },
                { label: 'Facebook Debugger', url: 'https://developers.facebook.com/tools/debug/' },
                { label: 'X Card Validator', url: 'https://cards-dev.twitter.com/validator' },
                { label: 'Google Rich Results', url: 'https://search.google.com/test/rich-results' },
                { label: 'Schema.org Validator', url: 'https://validator.schema.org/' },
              ].map(tool => (
                <a
                  key={tool.url}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '11px', color: '#2563eb', textDecoration: 'none',
                    padding: '4px 8px', background: '#eff6ff', borderRadius: '4px',
                  }}
                >
                  {tool.label} <ExternalLink size={10} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: 500,
              color: '#6b7280', background: 'white', border: '1px solid #d1d5db',
              borderRadius: '6px', cursor: 'pointer',
            }}
          >
            Back to Editor
          </button>
          <button
            onClick={onPublish}
            disabled={!canPublish}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 20px', fontSize: '13px', fontWeight: 600,
              color: canPublish ? 'white' : '#9ca3af',
              background: canPublish ? '#2E5E34' : '#f3f4f6',
              border: canPublish ? 'none' : '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: canPublish ? 'pointer' : 'not-allowed',
              opacity: canPublish ? 1 : 0.7,
            }}
          >
            <Send size={14} />
            {isPublished ? 'Update & Publish' : 'Publish Now'}
          </button>
        </div>
      </div>
    </div>
  );
};
